import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from './Navbar';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import MobileDownloadGate from './MobileDownloadGate';
import Home from './pages/Home';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import { SITE_URL, organizationSchema, websiteSchema } from './seo';
import './App.css';

// Secondary routes are code-split; the build-time pre-render (scripts/prerender.js)
// waits for them, so crawlers still get complete HTML.
const Help = lazy(() => import('./pages/Help'));
const HelpArticle = lazy(() => import('./pages/HelpArticle'));
const GettingStarted = lazy(() => import('./pages/GettingStarted'));
const Updates = lazy(() => import('./pages/Updates'));
const About = lazy(() => import('./pages/About'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const NotFound = lazy(() => import('./pages/NotFound'));
const DesktopMockupPage = lazy(() => import('./mockups/desktop/DesktopMockupPage'));

/* Routes + chrome, router-agnostic so the pre-renderer can wrap it in a StaticRouter. */
export function AppShell() {
  return (
    <>
      {/* Site-wide defaults; pages override title/description/canonical/og:url and may override og:image. */}
      <Helmet>
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="HorseStat" />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:image" content={`${SITE_URL}/social/horsestat-card.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="HorseStat: data-driven horse racing analysis" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${SITE_URL}/social/horsestat-card.png`} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema())}</script>
      </Helmet>
      <MobileDownloadGate>
      <div className="app">
        <ScrollToTop />
        <Navbar />
        <main>
          <Suspense fallback={<div className="route-loading" aria-hidden="true" />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help/:categoryId/:articleSlug" element={<HelpArticle />} />
              <Route path="/help/:categoryId" element={<Help />} />
              <Route path="/getting-started" element={<GettingStarted />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/mockups/desktop/:variant" element={<DesktopMockupPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
      </MobileDownloadGate>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
